import React, { useEffect, useRef } from "react";
import { cn } from "@/lib/utils";
import "./voice-orb-states.css";

const PARTICLES = [
  [17, 57, 2, 0], [24, 26, 3, .4], [31, 75, 2, .8], [38, 17, 2, 1.2],
  [45, 68, 3, 1.6], [53, 28, 2, 2], [61, 81, 2, 2.4], [69, 21, 3, 2.8],
  [76, 62, 2, 3.2], [84, 37, 2, 3.6], [91, 72, 3, 4], [10, 35, 2, 4.4],
];

export const VoiceOrb = ({ active = false, level = 0, mode = "idle" }) => {
  const sceneRef = useRef(null);
  const stateRef = useRef({ mode: "idle", level: 0 });
  const safeMode = ["idle", "listening", "thinking", "speaking"].includes(mode) ? mode : "idle";
  const safeLevel = Number.isFinite(level) ? Math.max(0, Math.min(1, level)) : 0;
  // Prop changes update the signal, never restart the continuous animation clock.
  stateRef.current = { mode: safeMode, level: safeLevel };
  useEffect(() => {
    const scene = sceneRef.current;
    const planets = [...scene.querySelectorAll(".orbit-planet")];
    const preference = window.matchMedia?.("(prefers-reduced-motion: reduce)");
    const weights = { idle: 1, listening: 0, thinking: 0, speaking: 0 };
    let frameId, lastTime = null, angle = 0, time = 0;
    let width = scene.clientWidth, height = scene.clientHeight;
    const resize = () => { width = scene.clientWidth; height = scene.clientHeight; };
    const observer = typeof ResizeObserver !== "undefined" ? new ResizeObserver(resize) : null;
    observer?.observe(scene);
    const paint = (now) => {
      const dt = lastTime === null ? 0 : Math.min((now - lastTime) / 1000, .05);
      lastTime = now;
      const reduced = Boolean(preference?.matches);
      const { mode: currentMode, level: currentLevel } = stateRef.current;
      Object.keys(weights).forEach(key => {
        weights[key] += ((key === currentMode ? 1 : 0) - weights[key]) * (1 - Math.exp(-dt * 4));
      });
      if (!reduced) {
        time += dt;
        angle += dt * (.13 * weights.idle + .28 * weights.listening + .95 * weights.thinking + .4 * weights.speaking);
      }
      const live = currentMode === "listening" || currentMode === "speaking";
      // Real capture/playback analyser only; no synthetic speech envelope here.
      const signal = !reduced && live ? currentLevel : 0;
      scene.style.setProperty("--voice-level", String(signal));
      const radius = Math.min(width * .38, 242);
      const radiusY = Math.min(height * .35, 114);
      planets.forEach((planet, index) => {
        const phase = angle + index * Math.PI * 2 / 3;
        const energy = reduced ? .15 : weights.listening * signal + weights.speaking * (.25 + .75 * signal) + weights.thinking * (.5 + .5 * Math.sin(time * 3 - index * 2));
        const distance = reduced ? 1 : 1 - weights.listening * (.1 + signal * .2) + weights.speaking * signal * .15;
        planet.style.transform = `translate(${(Math.cos(phase) * radius * distance).toFixed(2)}px,${(Math.sin(phase) * radiusY * distance).toFixed(2)}px) scale(${(1 + energy * .38).toFixed(3)})`;
        planet.style.setProperty("--planet-energy", energy.toFixed(3));
      });
      if (!reduced) frameId = requestAnimationFrame(paint);
    };
    const restart = () => {
      cancelAnimationFrame(frameId);
      lastTime = null;
      paint(performance.now());
    };
    preference?.addEventListener?.("change", restart);
    restart();
    return () => {
      cancelAnimationFrame(frameId);
      observer?.disconnect();
      preference?.removeEventListener?.("change", restart);
    };
  }, []);
  return (
  <div
    ref={sceneRef}
    className={cn("voice-orb-scene", "has-state-motion", active && "is-active", `is-${safeMode}`)}
    aria-hidden="true"
  >
    <div className="scene-halo" />
    <div className="scene-orbit orbit-one"><i /><i /></div>
    <div className="scene-orbit orbit-two"><i /></div>
    <div className="scene-orbit orbit-three"><i /><i /></div>
    <span className="orbit-planet planet-cyan" />
    <span className="orbit-planet planet-violet" />
    <span className="orbit-planet planet-blue" />
    <span className="orbit-star orbit-star-one" />
    <span className="orbit-star orbit-star-two" />
    <span className="orbit-star orbit-star-three" />
    <div className="orb-signal-field"><span /><span /><span /></div>
    <div className="glass-orb-wrap">
      <div className="glass-orb-aura" />
      <img className="glass-orb-image" src="/assets/voice-orb-glass-v1.png" alt="" />
      <div className="glass-orb-sheen" />
      <div className="glass-orb-ring ring-a" />
      <div className="glass-orb-ring ring-b" />
      <div className="orb-thought-light" />
      {PARTICLES.map(([x, y, size, delay], index) => (
        <span key={index} className="orb-particle" style={{ left: `${x}%`, top: `${y}%`, width: size, height: size, animationDelay: `${delay}s` }} />
      ))}
    </div>
  </div>
  );
};
